
import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Alert,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { getToken } from "firebase/app-check";
import { getAppCheckInstance } from "../../config/ConfigFirebase";
import { getToken as getAuthIdToken } from "../../services/auth/AuthServices";
import { Timestamp } from "@firebase/firestore";
import { pqrsdFunctionUrl } from "../../services/backend/PqrsdService";
import {
  PQRSD_ACCEPT_ATTR,
  PQRSD_ALLOWED_LABEL,
  PQRSD_MAX_FILES,
  PQRSD_MAX_FILE_BYTES,
  PQRSD_TYPES,
  isAllowedPqrsdMime,
} from "../../constants";
import "./PqrsdForm.css";

import { pqrsdPublicPath, pqrsdText } from "./pqrsdLabel";
// The owning internal user is resolved server-side by handlePqrsd; this form
// must never send meta.pqrsdOwner to avoid letting clients choose the owner.

// The select carries the type KEY the backend works with (SLA per type, the
// classifier, reports) and shows its label. Sending the label made every
// uncertain classification fall back to "peticion_general", so a solicitud de
// información got 15 days instead of 10 and a consulta 15 instead of 30.
const PQRSD_TYPE_ORDER = [
  "info",
  "consulta",
  "queja",
  "reclamo",
  "felicitacion",
  "sugerencia",
  "denuncia",
  "peticion_general",
];

const SOLICITOR_TYPES = [
  "Persona natural",
  "Niño, niña o adolescente",
  "Persona jurídica",
  "Apoderado",
];

const ID_TYPES = [
  "Cédula de Ciudadanía",
  "Cédula de Extranjería",
  "Tarjeta de Identidad",
  "Tarjeta de Movilidad Fronteriza",
  "Permiso especial de permanencia",
];

const STEPS = ["Anonimato", "Datos del solicitante", "Información PQRSD", "Revisión"];

const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + " " + sizes[i];
};

// Centralized sanitization helpers
const NA = "N/A";

const safe = (v) => {
  const s = String(v ?? "").trim();
  return s.length ? s : NA;
};

const anonVal = (isAnon, v) => (isAnon ? NA : safe(v));

// Helper for UI display (normalize accents and check if anonymous)
const isAnonDisplay = (val) => {
  const normalized = String(val ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return normalized === "si";
};

// Format date in Bogotá timezone: "DD/MM/YYYY HH:MM"
const formatBogotaDateTime = (dateInput = Date.now()) => {
  const d = new Date(dateInput);
  const parts = new Intl.DateTimeFormat("es-CO", {
    timeZone: "America/Bogota",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);

  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  return `${get("day")}/${get("month")}/${get("year")} ${get("hour")}:${get("minute")}`;
};

// Format date for display: "Día de la semana, Día de Mes de Año HH:MM (Bogotá)"
const formatBogotaDate = (dateString) => {
  const date = new Date(dateString);
  
  // Format parts separately to get exact format
  const weekdayFormatter = new Intl.DateTimeFormat("es-CO", {
    timeZone: "America/Bogota",
    weekday: "long",
  });
  
  const dateFormatter = new Intl.DateTimeFormat("es-CO", {
    timeZone: "America/Bogota",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  
  const timeFormatter = new Intl.DateTimeFormat("es-CO", {
    timeZone: "America/Bogota",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  
  const weekday = weekdayFormatter.format(date);
  const datePart = dateFormatter.format(date);
  const timePart = timeFormatter.format(date);
  
  // Format: "lunes, 15 de enero de 2024 10:30 (Bogotá)"
  return `${weekday}, ${datePart} ${timePart} (Bogotá)`;
};

  const validateIdNumber = (idNumber, idType, solicitorType) => {
    if (!idNumber) {
      return "Este campo es obligatorio";
    }

    const trimmedId = idNumber.trim();

    // Caso Persona Jurídica (NIT)
    if (solicitorType === "Persona jurídica") {
      if (!/^[0-9]+(-[0-9])?$/.test(trimmedId)) {
        return "El NIT debe contener solo números y opcionalmente un guion con el dígito de verificación.";
      }
      return null;
    }

    // Casos solo numéricos
    if (
      idType === "Cédula de Ciudadanía" ||
      idType === "Cédula de Extranjería" ||
      idType === "Tarjeta de Identidad"
    ) {
      if (!/^[0-9]+$/.test(trimmedId)) {
        return "El número de identificación debe contener solo números para el tipo seleccionado.";
      }
      return null;
    }

    // Casos alfanuméricos
    if (
      idType === "Tarjeta de Movilidad Fronteriza" ||
      idType === "Permiso especial de permanencia"
    ) {
      if (!/^[A-Za-z0-9]+$/.test(trimmedId)) {
        return "El número de identificación debe contener solo letras y números.";
      }
      return null;
    }

    return null;
  };

export default function PqrsdForm({ variant = "public" }) {
  // "window" = radicar por ventanilla: an authenticated staffer files a PQRSD on
  // behalf of a citizen. Same intake -> PDF -> email pipeline; only the channel,
  // the captured intake staffer, and a few copy strings differ from the public form.
  const isWindow = variant === "window";
  const navigate = useNavigate();
  const staffUser = useSelector((state) => state.user.value);
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [submitted, setSubmitted] = useState(false);
  const [submittedRadicado, setSubmittedRadicado] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState("");

  // One idempotency key per form lifetime; survives retries after transient errors.
  const idempotencyKey = useMemo(() => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return `pqrsd-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }, []);

  const [formData, setFormData] = useState({
    anonymous: "",
    email: "",
    solicitorType: "",
    name: "",
    surname: "",
    idType: "",
    idNumber: "",
    pqrsdType: "",
    pqrsdSubject: "",
    pqrsdBody: "",
    dataPolicyAccepted: false,
    attachments: [],
  });

  const validateStep = (step, showErrors = true) => {
    const newErrors = {};

    if (step === 0) {
      // Paso 1: Anonimato
      if (!formData.anonymous) {
        newErrors.anonymous = "Este campo es obligatorio";
      }
      // Anonymous citizens may leave it blank (no notifications, no online
      // tracking); a value, when given, must still be a valid address.
      const email = String(formData.email || "").trim();
      if (!email) {
        if (formData.anonymous !== "Sí") newErrors.email = "Este campo es obligatorio";
      } else if (!/\S+@\S+\.\S+/.test(email)) {
        newErrors.email = "Ingrese un correo electrónico válido";
      }
    } else if (step === 1 && formData.anonymous === "No") {
      // Paso 2: Datos del solicitante (solo si no es anónimo)
      if (!formData.solicitorType) {
        newErrors.solicitorType = "Este campo es obligatorio";
      }
      if (!formData.name) {
        newErrors.name = "Este campo es obligatorio";
      }
      if (!formData.surname) {
        newErrors.surname = "Este campo es obligatorio";
      }
      if (formData.solicitorType !== "Persona jurídica") {
        if (!formData.idType) {
          newErrors.idType = "Este campo es obligatorio";
        }
      }
      
      // Validación específica de idNumber
      const idNumberError = validateIdNumber(
        formData.idNumber,
        formData.solicitorType === "Persona jurídica" ? "NIT" : formData.idType,
        formData.solicitorType
      );
      if (idNumberError) {
        newErrors.idNumber = idNumberError;
      }
    } else if (
      (step === 1 && formData.anonymous === "Sí") ||
      (step === 2 && formData.anonymous === "No")
    ) {
      // Paso 3: Información PQRSD
      if (!formData.pqrsdType) {
        newErrors.pqrsdType = "Este campo es obligatorio";
      }
      if (!formData.pqrsdSubject) {
        newErrors.pqrsdSubject = "Este campo es obligatorio";
      }
      if (!formData.pqrsdBody) {
        newErrors.pqrsdBody = "Este campo es obligatorio";
      }
      if (!formData.dataPolicyAccepted) {
        newErrors.dataPolicyAccepted = "Debe aceptar la política de datos";
      }
    }

    if (showErrors) {
      setErrors(newErrors);
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleResetForm = () => {
    setFormData({
      anonymous: "",
      email: "",
      solicitorType: "",
      name: "",
      surname: "",
      idType: "",
      idNumber: "",
      pqrsdType: "",
      pqrsdSubject: "",
      pqrsdBody: "",
      dataPolicyAccepted: false,
      attachments: [],
    });
    setActiveStep(0);
    setErrors({});
    setSubmittedRadicado(null);
    setSubmitStatus(null);
    setSubmitted(false);
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };
      
      // Si cambia a anónimo, limpiar datos del solicitante y ajustar paso
      if (field === "anonymous" && value === "Sí") {
        newData.solicitorType = "";
        newData.name = "";
        newData.surname = "";
        newData.idType = "";
        newData.idNumber = "";
        // Si estamos en el paso 1 (Datos del solicitante), volver al paso 0
        if (activeStep === 1) {
          setActiveStep(0);
        }
        // Si estamos en paso 2 o 3, ajustar al paso correspondiente
        else if (activeStep === 2) {
          setActiveStep(1); // Paso 2 (PQRSD) en modo anónimo
        } else if (activeStep === 3) {
          setActiveStep(2); // Paso 3 (Revisión) en modo anónimo
        }
      }
      
      // Si cambia de anónimo a no anónimo y estamos en paso avanzado, ajustar
      if (field === "anonymous" && value === "No" && prev.anonymous === "Sí") {
        // Si estamos en paso 1 (PQRSD en modo anónimo), mantener en paso 2 (PQRSD en modo no anónimo)
        if (activeStep === 1) {
          setActiveStep(2);
        }
        // Si estamos en paso 2 (Revisión en modo anónimo), ir a paso 3 (Revisión en modo no anónimo)
        else if (activeStep === 2) {
          setActiveStep(3);
        }
      }
      
      // Si cambia a Persona jurídica, forzar idType
      if (field === "solicitorType" && value === "Persona jurídica") {
        newData.idType = "NIT";
      }
      
      return newData;
    });
    
    // Validar idNumber en tiempo real si cambia idNumber, idType o solicitorType
    if (field === "idNumber" || field === "idType" || field === "solicitorType") {
      // Calcular los valores que se usarán después del update
      let idTypeToValidate = formData.idType;
      let solicitorTypeToValidate = formData.solicitorType;
      let idNumberToValidate = formData.idNumber;
      
      if (field === "idNumber") {
        idNumberToValidate = value;
      } else if (field === "idType") {
        idTypeToValidate = value;
      } else if (field === "solicitorType") {
        solicitorTypeToValidate = value;
        if (value === "Persona jurídica") {
          idTypeToValidate = "NIT";
        }
      }
      
      const idNumberError = validateIdNumber(
        idNumberToValidate,
        solicitorTypeToValidate === "Persona jurídica" ? "NIT" : idTypeToValidate,
        solicitorTypeToValidate
      );
      
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (idNumberError) {
          newErrors.idNumber = idNumberError;
        } else {
          delete newErrors.idNumber;
        }
        return newErrors;
      });
    } else {
      // Limpiar error del campo cuando se modifica
      if (errors[field]) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  const handleFileChange = (event) => {
    const incoming = Array.from(event.target.files);
    event.target.value = "";

    // Mirror the cloud function's limits client-side so we never upload
    // something intakePqrsd will reject (max files, 10 MB each, allowed MIME).
    const accepted = [];
    const rejected = [];
    let slots = PQRSD_MAX_FILES - formData.attachments.length;
    incoming.forEach((file) => {
      if (slots <= 0) {
        rejected.push(`${file.name}: máximo ${PQRSD_MAX_FILES} archivos`);
      } else if (file.size > PQRSD_MAX_FILE_BYTES) {
        rejected.push(`${file.name}: supera 10 MB (${formatFileSize(file.size)})`);
      } else if (!isAllowedPqrsdMime(file.type)) {
        rejected.push(`${file.name}: tipo no permitido`);
      } else {
        accepted.push(file);
        slots -= 1;
      }
    });

    if (accepted.length) {
      setFormData((prev) => ({
        ...prev,
        attachments: [...prev.attachments, ...accepted],
      }));
    }
    setErrors((prev) => {
      const next = { ...prev };
      if (rejected.length) next.attachments = rejected.join(" · ");
      else delete next.attachments;
      return next;
    });
  };

  const handleRemoveFile = (index) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const handleEditStep = (step) => {
    setActiveStep(step);
  };

  const buildPayload = () => {
    // Calculate isAnon BEFORE building payload
    const isAnon = String(formData.anonymous ?? formData.anonymus ?? "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") === "si";

    // Use Firestore Timestamp instead of Date.now()
    const createdAt = Timestamp.now();
    const fecha = formatBogotaDateTime(createdAt.toMillis());

    // Determine idType (NIT for Persona jurídica, otherwise use formData.idType)
    const idTypeValue = formData.solicitorType === "Persona jurídica" 
      ? "NIT" 
      : formData.idType;

    // Build the PQRSD payload structure
    const finalPayload = {
      anonymous: formData.anonymous,
      email: String(formData.email || "").trim(),
      pqrsd: {
        type: formData.pqrsdType,
        subject: formData.pqrsdSubject,
        body: formData.pqrsdBody,
      },
      dataPolicy: "Acepto",
      attachments: formData.attachments.map((file) => ({
        name: file.name,
        size: file.size,
        type: file.type,
      })),
      createdAt: createdAt,
      fecha: fecha,
    };

    // Ventanilla: stamp the channel and the receiving staffer so handlePqrsd
    // can mark the record + timeline. intakeBy is also re-derived server-side
    // from the Authorization bearer; this client copy is the fallback.
    if (isWindow) {
      finalPayload.channel = "ventanilla";
      finalPayload.intakeBy = {
        userId: staffUser?.userId || null,
        name: staffUser?.name || null,
        email: staffUser?.email || null,
      };
    }

    // Build solicitor object with ALL fields (always present, even if anonymous)
    finalPayload.solicitor = {
      name: anonVal(isAnon, formData?.solicitor?.name ?? formData.name),
      surname: anonVal(isAnon, formData?.solicitor?.surname ?? formData.surname),
      idType: anonVal(isAnon, formData?.solicitor?.idType ?? idTypeValue),
      idNumber: anonVal(isAnon, formData?.solicitor?.idNumber ?? formData.idNumber),
      solicitorType: anonVal(isAnon, formData?.solicitor?.solicitorType ?? formData.solicitorType),
    };

    // 🔁 Compatibility fields (IMPORTANT) - Add as flat fields in pqrsd
    finalPayload.pqrsd.type_person = anonVal(isAnon, formData.solicitorType);
    finalPayload.pqrsd.nombre_solicitante = anonVal(isAnon, formData.name);
    finalPayload.pqrsd.apellido_solicitante = anonVal(isAnon, formData.surname);
    finalPayload.pqrsd.tipo_id = anonVal(isAnon, idTypeValue);
    finalPayload.pqrsd.num_id = anonVal(isAnon, formData.idNumber);



    // No client-side meta: handlePqrsd resolves the owning user + template
    // from its own env. Keeping the field for backwards compatibility in case
    // handlePqrsd reads `meta.constants` for telemetry tags.
    const metaObject = {
      constants: {
        source: "web",
        version: "2.0",
      },
    };

    return {
      pqrsd: finalPayload,
      meta: metaObject,
    };
  };

  const handleSubmit = async () => {
    if (!validateStep(activeStep)) {
      return;
    }

    // Prevent double submit
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSubmitMsg(pqrsdText("Enviando PQRSd..."));
    setSubmitting(true);
    setSubmitStatus(null);

    try {
      const payload = buildPayload();
      const intakeUrl =
        process.env.REACT_APP_PQRSD_INTAKE_URL ||
        process.env.VITE_PQRSD_INTAKE_URL ||
        pqrsdFunctionUrl("intakePqrsd");

      // App Check token from the singleton. Not force-refreshed: the SDK keeps
      // a valid one cached, and forced exchanges get throttled (HTTP 429).
      const appCheckInstance = getAppCheckInstance();
      if (!appCheckInstance) {
        throw new Error("App Check no está inicializado");
      }

      let appCheckToken = null;
      try {
        setSubmitMsg("Verificando seguridad...");
        const tokenResult = await getToken(appCheckInstance);
        appCheckToken = tokenResult?.token;
        
        // Guard: throw if token is missing
        if (!appCheckToken) {
          throw new Error("Token de App Check no disponible");
        }
        
        // Log token length for debugging
      } catch (error) {
        console.error("Error obteniendo token de App Check:", error);
        throw new Error("No se pudo obtener el token de App Check: " + error.message);
      }

      setSubmitMsg("Preparando datos...");
      const formDataToSend = new FormData();
      formDataToSend.append("payload", JSON.stringify(payload));

      formData.attachments.forEach((file) => {
        formDataToSend.append("files[]", file);
      });

      // Build headers with App Check token + idempotency key
      const headers = {
        "X-Firebase-AppCheck": appCheckToken,
        "X-Idempotency-Key": idempotencyKey,
      };

      // Ventanilla: attach the logged-in staffer's Firebase ID token so the
      // backend can verify and trust the recorded intake operator.
      if (isWindow) {
        try {
          const idToken = await getAuthIdToken();
          if (idToken) headers.Authorization = "Bearer " + idToken;
        } catch (_) {
          // Non-fatal: server falls back to the client-supplied intakeBy.
        }
      }

      setSubmitMsg("Enviando solicitud...");
      const response = await fetch(intakeUrl, {
        method: "POST",
        headers: headers,
        body: formDataToSend,
        // Do NOT set Content-Type manually for multipart/form-data
      });

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error(
            "Ha alcanzado el límite de solicitudes permitidas. Por favor espere unos minutos antes de intentarlo nuevamente."
          );
        }
        const errorText = await response.text().catch(() => `Error ${response.status}`);
        throw new Error(`Error al enviar: ${errorText}`);
      }

      const data = await response.json().catch(() => ({}));
      setSubmittedRadicado(data?.radicado || null);
      setSubmitStatus("success");
      setSubmitted(true);
    } catch (error) {
      console.error("Error al enviar PQRSD:", error);
      setSubmitStatus("error");
      setSubmitMsg(`Error: ${error.message}`);
      // Show error message to user
      setErrors((prev) => ({
        ...prev,
        submit: error.message || "Error al enviar la solicitud. Por favor, intente nuevamente.",
      }));
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  const getStepContent = (step) => {
    // Mapear el step visual al contenido correcto según anonimato
    // Si es anónimo: step 0 = Anonimato, step 1 = PQRSD, step 2 = Revisión
    // Si no es anónimo: step 0 = Anonimato, step 1 = Datos, step 2 = PQRSD, step 3 = Revisión
    
    if (step === 0) {
      // Paso 1: Anonimato
      // "Siguiente" stays disabled while the address is malformed; say why as
      // soon as something is typed instead of leaving a dead button.
      const typedEmail = String(formData.email || "").trim();
      const liveEmailError = typedEmail && !/\S+@\S+\.\S+/.test(typedEmail)
        ? "Ingrese un correo electrónico válido"
        : null;
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl component="fieldset" error={!!errors.anonymous}>
              <Typography variant="subtitle1" gutterBottom>
                ¿Desea realizar esta solicitud de forma anónima?
              </Typography>
              <RadioGroup
                row
                value={formData.anonymous}
                onChange={(e) => handleChange("anonymous", e.target.value)}
              >
                <FormControlLabel value="Sí" control={<Radio />} label="Sí" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
              {errors.anonymous && (
                <FormHelperText>{errors.anonymous}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required={formData.anonymous !== "Sí"}
              label={formData.anonymous === "Sí" ? "Correo electrónico (opcional)" : "Correo electrónico"}
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              error={!!(errors.email || liveEmailError)}
              helperText={
                errors.email
                || liveEmailError
                || (formData.anonymous === "Sí"
                  ? "Si no indica un correo no recibirá notificaciones; podrá consultar el estado en línea solo con el número de radicado, así que guárdelo."
                  : undefined)
              }
            />
          </Grid>
        </Grid>
      );
    } else if (step === 1 && formData.anonymous === "No") {
      // Paso 2: Datos del solicitante
      const isJuridica = formData.solicitorType === "Persona jurídica";

      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth required error={!!errors.solicitorType}>
              <InputLabel>Tipo de solicitante</InputLabel>
              <Select
                value={formData.solicitorType}
                onChange={(e) => handleChange("solicitorType", e.target.value)}
                label="Tipo de solicitante"
              >
                {SOLICITOR_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
              {errors.solicitorType && (
                <FormHelperText>{errors.solicitorType}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label={isJuridica ? "Razón social" : "Nombre"}
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label={isJuridica ? "Estructura legal" : "Apellido"}
              value={formData.surname}
              onChange={(e) => handleChange("surname", e.target.value)}
              error={!!errors.surname}
              helperText={errors.surname}
            />
          </Grid>

          {!isJuridica && (
            <Grid item xs={12}>
              <FormControl component="fieldset" required error={!!errors.idType}>
                <Typography variant="subtitle1" gutterBottom>
                  Tipo de identificación
                </Typography>
                <RadioGroup
                  row
                  value={formData.idType}
                  onChange={(e) => handleChange("idType", e.target.value)}
                >
                  {ID_TYPES.map((type) => (
                    <FormControlLabel
                      key={type}
                      value={type}
                      control={<Radio />}
                      label={type}
                    />
                  ))}
                </RadioGroup>
                {errors.idType && (
                  <FormHelperText>{errors.idType}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          )}

          {isJuridica && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Tipo de identificación: NIT
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Número de identificación"
              value={formData.idNumber}
              onChange={(e) => handleChange("idNumber", e.target.value)}
              error={!!errors.idNumber}
              helperText={errors.idNumber}
            />
          </Grid>
        </Grid>
      );
    } else if (
      (step === 1 && formData.anonymous === "Sí") ||
      (step === 2 && formData.anonymous === "No")
    ) {
      // Paso 3: Información PQRSD
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <FormControl fullWidth required error={!!errors.pqrsdType}>
              <InputLabel>{pqrsdText("Tipo de PQRSD")}</InputLabel>
              <Select
                value={formData.pqrsdType}
                onChange={(e) => handleChange("pqrsdType", e.target.value)}
                label={pqrsdText("Tipo de PQRSD")}
              >
                {PQRSD_TYPE_ORDER.map((key) => (
                  <MenuItem key={key} value={key}>
                    {PQRSD_TYPES[key]}
                  </MenuItem>
                ))}
              </Select>
              {errors.pqrsdType && (
                <FormHelperText>{errors.pqrsdType}</FormHelperText>
              )}
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Asunto"
              value={formData.pqrsdSubject}
              onChange={(e) => handleChange("pqrsdSubject", e.target.value)}
              error={!!errors.pqrsdSubject}
              helperText={errors.pqrsdSubject}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Descripción"
              multiline
              rows={6}
              value={formData.pqrsdBody}
              onChange={(e) => handleChange("pqrsdBody", e.target.value)}
              error={!!errors.pqrsdBody}
              helperText={errors.pqrsdBody}
            />
          </Grid>

          <Grid item xs={12}>
            <Box>
              <input
                accept={PQRSD_ACCEPT_ATTR}
                style={{ display: "none" }}
                id="file-upload"
                multiple
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="file-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<AttachFileIcon />}
                >
                  Adjuntar archivos
                </Button>
              </label>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 0.5 }}
              >
                {PQRSD_ALLOWED_LABEL}. Máximo {PQRSD_MAX_FILES} archivos, 10 MB cada uno.
              </Typography>
              {errors.attachments && (
                <Typography variant="caption" color="error" display="block">
                  {errors.attachments}
                </Typography>
              )}
            </Box>

            {formData.attachments.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Archivos adjuntos:
                </Typography>
                {formData.attachments.map((file, index) => (
                  <Chip
                    key={index}
                    label={`${file.name} (${formatFileSize(file.size)})`}
                    onDelete={() => handleRemoveFile(index)}
                    deleteIcon={<DeleteIcon />}
                    sx={{ m: 0.5 }}
                  />
                ))}
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.dataPolicyAccepted}
                  onChange={(e) =>
                    handleChange("dataPolicyAccepted", e.target.checked)
                  }
                />
              }
              label={
                <Typography variant="body2">
                  Acepto la política de tratamiento de datos personales. Mis
                  datos están protegidos y se usarán únicamente para dar
                  trámite a esta solicitud.
                </Typography>
              }
            />
            {errors.dataPolicyAccepted && (
              <FormHelperText error>{errors.dataPolicyAccepted}</FormHelperText>
            )}
          </Grid>
        </Grid>
      );
    } else if (
      (step === 2 && formData.anonymous === "Sí") ||
      (step === 3 && formData.anonymous === "No")
    ) {
      // Paso 4: Revisión
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Revisa tu solicitud
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Anonimato
              </Typography>
              <Typography variant="body1">{formData.anonymous}</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Correo electrónico
              </Typography>
              <Typography variant="body1">
                {String(formData.email || "").trim() || <em>Sin correo</em>}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Tipo de solicitante
              </Typography>
              <Typography variant="body1">
                {anonVal(isAnonDisplay(formData.anonymous), formData.solicitorType)}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Nombre completo
              </Typography>
              <Typography variant="body1">
                {isAnonDisplay(formData.anonymous)
                  ? NA
                  : `${safe(formData.name)} ${safe(formData.surname)}`.trim() || NA}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Tipo de identificación
              </Typography>
              <Typography variant="body1">
                {isAnonDisplay(formData.anonymous)
                  ? NA
                  : formData.solicitorType === "Persona jurídica"
                  ? "NIT"
                  : safe(formData.idType)}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Número de identificación
              </Typography>
              <Typography variant="body1">
                {anonVal(isAnonDisplay(formData.anonymous), formData.idNumber)}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {pqrsdText("Tipo de PQRSD")}
              </Typography>
              <Typography variant="body1">{PQRSD_TYPES[formData.pqrsdType] || formData.pqrsdType}</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Asunto
              </Typography>
              <Typography variant="body1">{formData.pqrsdSubject}</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Descripción
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                {formData.pqrsdBody}
              </Typography>
            </Paper>
          </Grid>

          {formData.attachments.length > 0 && (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Anexos
                </Typography>
                {formData.attachments.map((file, index) => (
                  <Typography key={index} variant="body2">
                    • {file.name} ({formatFileSize(file.size)})
                  </Typography>
                ))}
              </Paper>
            </Grid>
          )}

          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Política de datos
              </Typography>
              <Typography variant="body1">Acepto</Typography>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Fecha de radicación
              </Typography>
              <Typography variant="body1">
                {formatBogotaDate(new Date().toISOString())}
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {formData.anonymous === "No" && (
                <Button
                  variant="outlined"
                  onClick={() => handleEditStep(1)}
                >
                  Editar datos personales
                </Button>
              )}
              <Button
                variant="outlined"
                onClick={() =>
                  handleEditStep(formData.anonymous === "Sí" ? 1 : 2)
                }
              >
                {pqrsdText("Editar PQRSD")}
              </Button>
            </Box>
          </Grid>
        </Grid>
      );
    }

    return null;
  };

  // Determinar qué pasos mostrar según anonimato
  const getSteps = () => {
    if (formData.anonymous === "Sí") {
      return ["Anonimato", pqrsdText("Información PQRSD"), "Revisión"];
    }
    return STEPS.map(pqrsdText);
  };

  const steps = getSteps();

  // Pantalla de agradecimiento
  if (submitted) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <Paper
            elevation={2}
            sx={{ p: 4, textAlign: "center", maxWidth: 560, width: "100%" }}
          >
          <Typography variant="h4" component="h1" gutterBottom>
            {isWindow ? pqrsdText("PQRSD radicada con éxito") : "Gracias por enviar su solicitud"}
          </Typography>
          {submittedRadicado && (
            <Box sx={{ my: 3 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Número de radicado
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontFamily: "monospace",
                  letterSpacing: 1,
                  fontWeight: 600,
                  mt: 1,
                }}
              >
                {submittedRadicado}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {isWindow
                  ? "Entregue este número al ciudadano. Lo necesitará para consultar el estado."
                  : "Guarde este número. Lo necesitará para consultar el estado."}
              </Typography>
            </Box>
          )}
          <Typography variant="body1" sx={{ mt: 3, mb: 4 }}>
            {!String(formData.email || "").trim() ? (
              <>
                La solicitud se radicó <strong>sin correo electrónico</strong>: no
                {isWindow ? " se enviarán" : " recibirá"} notificaciones. El estado se
                consulta en línea solo con el número de radicado, dejando el correo
                vacío: consérvelo.
              </>
            ) : isWindow ? (
              <>
                Se envió una confirmación con el radicado al correo electrónico
                del ciudadano. Podrá consultar el estado en cualquier momento
                desde la página <strong>{pqrsdPublicPath("estado")}</strong>.
              </>
            ) : (
              <>
                Enviaremos una confirmación con el radicado al correo electrónico
                que indicó. También podrá consultar el estado en cualquier momento
                desde la página <strong>{pqrsdPublicPath("estado")}</strong>.
              </>
            )}
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            sx={{ mt: 2 }}
          >
            <Button variant="contained" onClick={handleResetForm}>
              {isWindow ? pqrsdText("Radicar otra PQRSD") : "Radicar otra solicitud"}
            </Button>
            {!isWindow && (
              <Button
                variant="outlined"
                onClick={() => navigate(pqrsdPublicPath("estado"))}
              >
                Consultar estado
              </Button>
            )}
          </Stack>
        </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        {isWindow ? pqrsdText("Radicar PQRSD por ventanilla") : pqrsdText("Radicar PQRSD")}
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mt: 4, mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Paper elevation={2} sx={{ p: 4 }}>
        {submitStatus === "success" && (
          <Alert severity="success" sx={{ mb: 3 }}>
            ¡Solicitud enviada con éxito!
          </Alert>
        )}

        {submitStatus === "error" && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errors.submit || "Error al enviar la solicitud. Por favor, intente nuevamente."}
          </Alert>
        )}

        {getStepContent(activeStep)}

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Atrás
          </Button>

          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting || submitting || !validateStep(activeStep, false)}
            >
              {submitting || isSubmitting ? "Enviando..." : "Confirmar y enviar"}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!validateStep(activeStep, false)}
            >
              Siguiente
            </Button>
          )}
        </Box>
      </Paper>

      {/* Loading overlay */}
      {isSubmitting && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 24,
              width: "min(420px,92vw)",
              textAlign: "center",
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 8 }}>
              Procesando tu solicitud…
            </div>
            <div style={{ opacity: 0.8, marginBottom: 16 }}>
              {submitMsg || "Esto puede tardar unos segundos. No cierres esta ventana."}
            </div>
            <div className="spinner" />
            <style>{`
              .spinner {
                width: 34px;
                height: 34px;
                margin: 0 auto;
                border: 4px solid rgba(0,0,0,0.1);
                border-top-color: rgba(0,0,0,0.6);
                border-radius: 50%;
                animation: spin 1s linear infinite;
              }
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      )}
    </Container>
  );
}

